export type LocaleState = {
  language: string; // e.g. "sv", "en"
  market: string;   // e.g. "se", "no"
};

export const DEFAULT_LOCALE: LocaleState = { language: "sv", market: "se" };

export function normalizeLocale(input: Partial<LocaleState> | null | undefined): LocaleState {
  const rawLanguage = (input?.language || DEFAULT_LOCALE.language).toLowerCase();
  // Canonicalize Norwegian: use 'nb' (Bokmål). Keep accepting legacy 'no'.
  const language = rawLanguage === "no" ? "nb" : rawLanguage;
  const market = (input?.market || DEFAULT_LOCALE.market).toLowerCase();
  return { language, market };
}

export function getRuntimeLocale(): LocaleState {
  try {
    const w = window as any;
    const cfg = w?.__WW__;
    // Align widget locale behavior with adapter-side recommendation requests:
    // prefer URL locale (e.g. /en-se/...) and fall back to runtime config.
    const fromPath = parseLocaleFromPathname(window.location.pathname);
    if (fromPath) return fromPath;
    return normalizeLocale({ language: cfg?.language, market: cfg?.market });
  } catch {
    return DEFAULT_LOCALE;
  }
}

export function setRuntimeLocale(next: LocaleState): void {
  try {
    const w = window as any;
    const existing = (w.__WW__ && typeof w.__WW__ === "object") ? w.__WW__ : {};
    w.__WW__ = { ...existing, ...normalizeLocale(next) };
  } catch {
    // ignore
  }
}

export function parseLocaleFromPathname(pathname: string): LocaleState | null {
  // Convention: /en-se/... => language=en, market=se
  const m = String(pathname || "").match(/^\/([a-z]{2})-([a-z]{2})(?:\/|$)/i);
  if (!m) return null;
  return normalizeLocale({ language: m[1], market: m[2] });
}

export function getCurrencyForMarket(market: string): string {
  switch (String(market || "").toLowerCase()) {
    case "se":
      return "SEK";
    case "no":
      return "NOK";
    case "dk":
      return "DKK";
    case "fi":
      return "EUR";
    case "de":
      return "EUR";
    case "fr":
      return "EUR";
    case "nl":
      return "EUR";
    case "us":
      return "USD";
    case "gb":
      return "GBP";
    default:
      return "EUR";
  }
}

export function toUiLocale(locale: LocaleState): string {
  // Basic BCP-47 tag. Good enough for formatting and future extension.
  return `${locale.language}-${locale.market.toUpperCase()}`;
}

export function formatMoney(amount?: number, locale: LocaleState = getRuntimeLocale()): string {
  if (typeof amount !== "number" || Number.isNaN(amount)) return "";
  const currency = getCurrencyForMarket(locale.market);
  const uiLocale = toUiLocale(locale);
  try {
    // Match existing UI behavior: no decimals
    return new Intl.NumberFormat(uiLocale, {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    // Fallback: keep it readable, never throw in UI
    return `${amount} ${currency}`;
  }
}

type Dict = Record<string, string>;

const SV: Dict = {
  "common.close": "Stäng",
  "common.retry": "Försök igen",
  "common.results": "Resultat",
  "common.hits": "träffar",
  "common.thinking": "Tänker...",
  "common.searching_products": "Söker efter produkter...",
  "common.error_generic": "Ett fel inträffade. Försök igen.",
  "common.loading": "Laddar...",
  "common.rate_limited_wait": "Vi är för närvarande hastighetsbegränsade. Vänta {seconds}s och försök igen.",
  "common.too_fast_wait": "Du skickar för snabbt. Försök igen om {seconds}s.",
  "common.auth_failed_reload": "Behörigheten misslyckades. Kontrollera tillåtna domäner för butiken och ladda om sidan.",

  "chat.placeholder": "Skriv här...",
  "search.placeholder": "Sök hur du vill...",
  "search.free_title": "Vad letar du efter...?",
  "search.free_subtitle": "Berätta vad du letar efter så visar vi de bästa träffarna.",
  "simple_search.no_results": "Tyvärr hittade vi inga resultat.",
  "simple_search.start_over": "Börja om",
  "simple_search.filter": "Filter",
  "simple_search.sort": "Sortera",
  "simple_search.filter_toggle": "Filtrera",
  "simple_search.sort_toggle": "Sortera",
  "simple_search.apply": "Använd",
  "simple_search.sort.relevance": "Rekommenderad",
  "simple_search.sort.recent": "Senast inkommet",
  "simple_search.sort.price_asc": "Pris, lågt till högt",
  "simple_search.sort.price_desc": "Pris, högt till lågt",
  "simple_search.oj_summary": "Visar {count} produkter för {query}",

  "filter.key.size": "Storlek",
  "filter.key.color": "Färg",
  "filter.key.brand": "Märke",
  "filter.key.material": "Material",
  "filter.key.model": "Modell",
  "filter.key.category": "Kategori",
  "filter.key.fabric_quality": "Tygkvalitet",
  "filter.key.price": "Pris",
  "filter.key.variant_number": "Färgkod",

  "filter.color.black": "Svart",
  "filter.color.white": "Vit",
  "filter.color.grey": "Grå",
  "filter.color.red": "Röd",
  "filter.color.orange": "Orange",
  "filter.color.yellow": "Gul",
  "filter.color.green": "Grön",
  "filter.color.blue": "Blå",
  "filter.color.dark_blue": "Mörkblå",
  "filter.color.purple": "Lila",
  "filter.color.pink": "Rosa",
  "filter.color.brown": "Brun",
  "filter.color.beige": "Beige",

  "browser.refine_hint": "Om detta inte var vad du letade efter, försök att formulera om.",
  "browser.explain_question": "Förklara frågan",
  "browser.input_placeholder": "Filtrera på vad du vill eller svara på frågan ovan...",
  "browser.show_more": "Visa fler",
  "browser.continue_to_category": "Fortsätt till {category}",

  "product.no_images": "Inga bilder tillgängliga",
  "product.adding_to_cart": "Lägger till…",
  "product.add_to_cart": "Lägg till i kundvagn",
  "product.read_more": "Läs mer om produkten",

  "product_qa.title": "Frågor?",
  "product_qa.subtitle": "Ställ en fråga om produkten. Ibland kan AI ha fel men jag kommer hjälpa dig så gott jag kan.",
  "product_qa.placeholder": "Skriv din fråga...",
  "product_qa.question_was": "Här är frågan som ställdes",
  "product_qa.new_question": "Ny fråga",
  "product_qa.show_more": "Visa mer",
  "product_qa.show_less": "Visa mindre",
  "product_qa.panel_title": "Fråga om produkten",
  "product_qa.panel_placeholder": "Ställ en följdfråga...",
  "product_qa.panel_close": "Stäng",

  "prompt.not_important": "Inte viktigt",
  "prompt.prefix_answer": "Svar:",

  "guided.choose_above": "Välj ett val ovan",

  "guided.refine": "Justera sökning",
  "guided.new_search": "Ny sökning",
  "guided.ask_products": "Fråga om produkterna",
  "prefix.refine": "Min justering:",
  "prefix.new_search": "Ny sökning:",
  "prefix.question": "Min fråga:",
};

const EN: Dict = {
  "common.close": "Close",
  "common.retry": "Try again",
  "common.results": "Results",
  "common.hits": "results",
  "common.thinking": "Thinking...",
  "common.searching_products": "Searching for products...",
  "common.error_generic": "Something went wrong. Please try again.",
  "common.loading": "Loading...",
  "common.rate_limited_wait": "We’re currently rate limited. Please wait {seconds}s and try again.",
  "common.too_fast_wait": "We’re sending too fast. Please try again in {seconds}s.",
  "common.auth_failed_reload": "Authorization failed. Please verify allowed domains for this store and reload the page.",

  "chat.placeholder": "Type here...",
  "search.placeholder": "What are you looking for?",
  "search.free_title": "Search freely",
  "search.free_subtitle": "Tell us what you're looking for and we’ll show the best matches.",
  "simple_search.no_results": "Sorry, no results.",
  "simple_search.start_over": "Start over",
  "simple_search.filter": "Filter",
  "simple_search.sort": "Sort",
  "simple_search.filter_toggle": "Filter",
  "simple_search.sort_toggle": "Sort",
  "simple_search.apply": "Apply",
  "simple_search.sort.relevance": "Relevance",
  "simple_search.sort.recent": "Latest",
  "simple_search.sort.price_asc": "Price, low to high",
  "simple_search.sort.price_desc": "Price, high to low",
  "simple_search.oj_summary": "Showing {count} products for {query}",

  "filter.key.size": "Size",
  "filter.key.color": "Color",
  "filter.key.brand": "Brand",
  "filter.key.material": "Material",
  "filter.key.model": "Model",
  "filter.key.category": "Category",
  "filter.key.fabric_quality": "Fabric quality",
  "filter.key.price": "Price",
  "filter.key.variant_number": "Color code",

  "filter.color.black": "Black",
  "filter.color.white": "White",
  "filter.color.grey": "Grey",
  "filter.color.red": "Red",
  "filter.color.orange": "Orange",
  "filter.color.yellow": "Yellow",
  "filter.color.green": "Green",
  "filter.color.blue": "Blue",
  "filter.color.dark_blue": "Dark blue",
  "filter.color.purple": "Purple",
  "filter.color.pink": "Pink",
  "filter.color.brown": "Brown",
  "filter.color.beige": "Beige",

  "browser.refine_hint": "If this wasn't what you were looking for, try rephrasing.",
  "browser.explain_question": "Explain the question",
  "browser.input_placeholder": "Refine your search or answer the question above...",
  "browser.show_more": "Show more",
  "browser.continue_to_category": "Continue to {category}",

  "product.no_images": "No images available",
  "product.adding_to_cart": "Adding…",
  "product.add_to_cart": "Add to cart",
  "product.read_more": "Read more about the product",

  "product_qa.title": "Questions?",
  "product_qa.subtitle": "Ask a question about the products. We’ll answer clearly and helpfully.",
  "product_qa.placeholder": "Type your question...",
  "product_qa.question_was": "Here’s the question that was asked",
  "product_qa.new_question": "New question",
  "product_qa.show_more": "Show more",
  "product_qa.show_less": "Show less",
  "product_qa.panel_title": "Ask about the product",
  "product_qa.panel_placeholder": "Ask a follow-up question...",
  "product_qa.panel_close": "Close",

  "prompt.not_important": "Not important",
  "prompt.prefix_answer": "Answer:",

  "guided.choose_above": "Choose an option above",

  "guided.refine": "Refine search",
  "guided.new_search": "New search",
  "guided.ask_products": "Ask about the products",
  "prefix.refine": "My refinement:",
  "prefix.new_search": "New search:",
  "prefix.question": "My question:",
};

const DA: Dict = {
  "common.close": "Luk",
  "common.retry": "Prøv igen",
  "common.results": "Resultater",
  "common.hits": "resultater",
  "common.thinking": "Tænker...",
  "common.searching_products": "Søger efter produkter...",
  "common.error_generic": "Der opstod en fejl. Prøv igen.",
  "common.loading": "Indlæser...",
  "common.rate_limited_wait": "Vi er i øjeblikket hastighedsbegrænset. Vent {seconds}s og prøv igen.",
  "common.too_fast_wait": "Du sender for hurtigt. Prøv igen om {seconds}s.",
  "common.auth_failed_reload": "Godkendelse mislykkedes. Tjek tilladte domæner for butikken og genindlæs siden.",

  "chat.placeholder": "Skriv her...",
  "search.placeholder": "Hvad leder du efter?",
  "search.free_title": "Søg helt frit",
  "search.free_subtitle": "Fortæl hvad du leder efter, så viser vi de bedste resultater.",
  "simple_search.no_results": "Beklager, ingen resultater.",
  "simple_search.start_over": "Start forfra",
  "simple_search.filter": "Filter",
  "simple_search.sort": "Sorter",
  "simple_search.filter_toggle": "Filtrér",
  "simple_search.sort_toggle": "Sortér",
  "simple_search.apply": "Anvend",
  "simple_search.sort.relevance": "Relevans",
  "simple_search.sort.recent": "Nyeste",
  "simple_search.sort.price_asc": "Pris, lav til høj",
  "simple_search.sort.price_desc": "Pris, høj til lav",
  "simple_search.oj_summary": "Viser {count} produkter for {query}",

  "filter.key.size": "Størrelse",
  "filter.key.color": "Farve",
  "filter.key.brand": "Mærke",
  "filter.key.material": "Materiale",
  "filter.key.model": "Model",
  "filter.key.category": "Kategori",
  "filter.key.fabric_quality": "Stofkvalitet",
  "filter.key.price": "Pris",
  "filter.key.variant_number": "Farvekode",

  "filter.color.black": "Sort",
  "filter.color.white": "Hvid",
  "filter.color.grey": "Grå",
  "filter.color.red": "Rød",
  "filter.color.orange": "Orange",
  "filter.color.yellow": "Gul",
  "filter.color.green": "Grøn",
  "filter.color.blue": "Blå",
  "filter.color.dark_blue": "Mørkeblå",
  "filter.color.purple": "Lilla",
  "filter.color.pink": "Pink",
  "filter.color.brown": "Brun",
  "filter.color.beige": "Beige",

  "browser.refine_hint": "Hvis det ikke var det, du ledte efter, så prøv at omformulere.",
  "browser.explain_question": "Forklar spørgsmålet",
  "browser.input_placeholder": "Forfin søgningen eller besvar spørgsmålet ovenfor...",
  "browser.show_more": "Vis flere",
  "browser.continue_to_category": "Fortsæt til {category}",

  "product.no_images": "Ingen billeder tilgængelige",
  "product.adding_to_cart": "Tilføjer…",
  "product.add_to_cart": "Læg i kurv",
  "product.read_more": "Læs mere om produktet",

  "product_qa.title": "Spørgsmål?",
  "product_qa.subtitle": "Stil et spørgsmål om produkterne. Vi svarer tydeligt og hjælpsomt.",
  "product_qa.placeholder": "Skriv dit spørgsmål...",
  "product_qa.question_was": "Her er spørgsmålet, der blev stillet",
  "product_qa.new_question": "Nyt spørgsmål",
  "product_qa.show_more": "Vis mere",
  "product_qa.show_less": "Vis mindre",
  "product_qa.panel_title": "Spørg om produktet",
  "product_qa.panel_placeholder": "Stil et opfølgende spørgsmål...",
  "product_qa.panel_close": "Luk",

  "prompt.not_important": "Ikke vigtigt",
  "prompt.prefix_answer": "Svar:",

  "guided.choose_above": "Vælg en mulighed ovenfor",

  "guided.refine": "Forfin søgning",
  "guided.new_search": "Ny søgning",
  "guided.ask_products": "Spørg om produkterne",
  "prefix.refine": "Min forfining:",
  "prefix.new_search": "Ny søgning:",
  "prefix.question": "Mit spørgsmål:",
};

const NO: Dict = {
  "common.close": "Lukk",
  "common.retry": "Prøv igjen",
  "common.results": "Resultater",
  "common.hits": "treff",
  "common.thinking": "Tenker...",
  "common.searching_products": "Søker etter produkter...",
  "common.error_generic": "Noe gikk galt. Prøv igjen.",
  "common.loading": "Laster...",
  "common.rate_limited_wait": "Vi er for øyeblikket hastighetsbegrenset. Vent {seconds}s og prøv igjen.",
  "common.too_fast_wait": "Du sender for raskt. Prøv igjen om {seconds}s.",
  "common.auth_failed_reload": "Autorisasjon mislyktes. Sjekk tillatte domener for butikken og last siden på nytt.",

  "chat.placeholder": "Skriv her...",
  "search.placeholder": "Hva leter du etter?",
  "search.free_title": "Søk helt fritt",
  "search.free_subtitle": "Fortell hva du leter etter, så viser vi de beste treffene.",
  "simple_search.no_results": "Beklager, ingen resultater.",
  "simple_search.start_over": "Start på nytt",
  "simple_search.filter": "Filter",
  "simple_search.sort": "Sorter",
  "simple_search.filter_toggle": "Filtrer",
  "simple_search.sort_toggle": "Sorter",
  "simple_search.apply": "Bruk",
  "simple_search.sort.relevance": "Relevans",
  "simple_search.sort.recent": "Nyeste",
  "simple_search.sort.price_asc": "Pris, lav til høy",
  "simple_search.sort.price_desc": "Pris, høy til lav",
  "simple_search.oj_summary": "Viser {count} produkter for {query}",

  "filter.key.size": "Størrelse",
  "filter.key.color": "Farge",
  "filter.key.brand": "Merke",
  "filter.key.material": "Materiale",
  "filter.key.model": "Modell",
  "filter.key.category": "Kategori",
  "filter.key.fabric_quality": "Stoffkvalitet",
  "filter.key.price": "Pris",
  "filter.key.variant_number": "Fargekode",

  "filter.color.black": "Svart",
  "filter.color.white": "Hvit",
  "filter.color.grey": "Grå",
  "filter.color.red": "Rød",
  "filter.color.orange": "Oransje",
  "filter.color.yellow": "Gul",
  "filter.color.green": "Grønn",
  "filter.color.blue": "Blå",
  "filter.color.dark_blue": "Mørkeblå",
  "filter.color.purple": "Lilla",
  "filter.color.pink": "Rosa",
  "filter.color.brown": "Brun",
  "filter.color.beige": "Beige",

  "browser.refine_hint": "Hvis dette ikke var det du lette etter, prøv å omformulere.",
  "browser.explain_question": "Forklar spørsmålet",
  "browser.input_placeholder": "Avgrens søket eller svar på spørsmålet ovenfor...",
  "browser.show_more": "Vis flere",
  "browser.continue_to_category": "Fortsett til {category}",

  "product.no_images": "Ingen bilder tilgjengelig",
  "product.adding_to_cart": "Legger til…",
  "product.add_to_cart": "Legg i handlekurv",
  "product.read_more": "Les mer om produktet",

  "product_qa.title": "Spørsmål?",
  "product_qa.subtitle": "Still et spørsmål om produktene. Vi svarer tydelig og hjelpsomt.",
  "product_qa.placeholder": "Skriv spørsmålet ditt...",
  "product_qa.question_was": "Her er spørsmålet som ble stilt",
  "product_qa.new_question": "Nytt spørsmål",
  "product_qa.show_more": "Vis mer",
  "product_qa.show_less": "Vis mindre",
  "product_qa.panel_title": "Spør om produktet",
  "product_qa.panel_placeholder": "Still et oppfølgingsspørsmål...",
  "product_qa.panel_close": "Lukk",

  "prompt.not_important": "Ikke viktig",
  "prompt.prefix_answer": "Svar:",

  "guided.choose_above": "Velg et alternativ ovenfor",

  "guided.refine": "Avgrens søk",
  "guided.new_search": "Nytt søk",
  "guided.ask_products": "Spør om produktene",
  "prefix.refine": "Min avgrensning:",
  "prefix.new_search": "Nytt søk:",
  "prefix.question": "Mitt spørsmål:",
};

const DICTS: Record<string, Dict> = { sv: SV, en: EN, da: DA, no: NO, nb: NO };

export function t(key: string, locale: LocaleState = getRuntimeLocale()): string {
  const dict = DICTS[locale.language] || DICTS.sv;
  return dict[key] || DICTS.sv[key] || key;
}


